<script setup lang="ts">
import { isAppLocale } from '~/constants/appLocale'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import Dialog from '~/components/common/Dialog.vue'
import type { MarketingConsentSource } from '~/constants/marketingConsent'
import { NEWSLETTER_PROMPT_EVENT, type NewsletterPromptDetail } from '~/composables/useNewsletterPrompt'

/** Where the dialog sits, stored with the consent: 'blog', or 'download' after a free PDF. */
const props = withDefaults(defineProps<{ source?: MarketingConsentSource }>(), { source: 'newsletter' })

const { t, locale } = useI18n()
const { consentText } = useMarketingConsent()

const isOpen = ref(false)
const email = ref('')
const loading = ref(false)
const errorMsg = ref('')
const success = ref(false)
/** Double opt-in: `sent` means check the inbox, `confirmed` was on the list already. */
const resultStatus = ref<'sent' | 'confirmed'>('sent')
/** Opened by ?newsletter=1 (a link from elsewhere), not by the page's own prompt. */
const openedByLink = ref(false)
/** The CV downloaded before the prompt, if any (see useNewsletterPrompt). */
const generationId = ref<string | null>(null)

function validateEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

function writeFlag(key: string) {
  try {
    localStorage.setItem(key, 'true')
  } catch {
    // Private mode / blocked storage: the dialog may show again next visit, which is acceptable.
  }
}

/**
 * Opens the dialog at most once per browser, and only when the visitor is not
 * already subscribed. There is no timer on purpose: callers ask for the prompt
 * through `useNewsletterPrompt()` after the visitor got something useful.
 */
function openIfEligible(force = false) {
  if (!import.meta.client) return
  const alreadySubscribed = readFlag('newsletter_subscribed')
  const alreadyShown = readFlag('newsletter_prompt_shown')
  if (force || (!alreadySubscribed && !alreadyShown)) {
    isOpen.value = true
    writeFlag('newsletter_prompt_shown')
  }
}

function onPromptEvent(event: Event) {
  const detail = (event as CustomEvent<NewsletterPromptDetail | undefined>).detail
  const prefill = detail?.email?.trim()
  if (prefill && !email.value) email.value = prefill
  if (detail?.generationId) generationId.value = detail.generationId
  openIfEligible(false)
}

async function submit() {
  errorMsg.value = ''
  if (!validateEmail(email.value)) {
    errorMsg.value = t('common.validators.email')
    return
  }
  try {
    loading.value = true
    const response = await $fetch<{ status: 'sent' | 'confirmed' }>('/api/newsletter/subscribe', {
      method: 'POST',
      body: {
        email: email.value,
        page_path: window.location.pathname,
        locale: isAppLocale(locale.value) ? locale.value : 'en',
        source: openedByLink.value ? 'newsletter' : props.source,
        generationId: generationId.value,
      }
    })
    resultStatus.value = response.status
    success.value = true
    writeFlag('newsletter_subscribed')
    // Long enough to read "check your inbox".
    setTimeout(() => { isOpen.value = false }, 5000)
  } catch {
    errorMsg.value = t('newsletter.error')
  } finally {
    loading.value = false
  }
}

/* SofaxInput wraps the native input, so focus goes through its root element. */
const emailInputRef = ref<ComponentPublicInstance | null>(null)

watch(isOpen, async (open) => {
  if (open) {
    await nextTick()
    const root = emailInputRef.value?.$el as HTMLElement | undefined
    root?.querySelector('input')?.focus()
  }
})

onMounted(() => {
  window.addEventListener(NEWSLETTER_PROMPT_EVENT, onPromptEvent)

  const url = new URL(window.location.href)
  if (url.searchParams.get('newsletter') === '1') {
    openedByLink.value = true
    openIfEligible(true)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener(NEWSLETTER_PROMPT_EVENT, onPromptEvent)
})
</script>

<template>
  <Dialog v-model="isOpen" size="md" :header="false" :footer="false" :closable="true" :dialog-class="'p-0'" content-class="!p-0">
    <div class="sofax relative overflow-hidden rounded-[0.625rem] bg-white p-6 sm:p-8 dark:bg-gray-900">
      <div class="grid gap-5">
        <div class="flex items-center justify-center">
          <span class="tag gap-2 !text-sm">
            <i class="bi bi-stars text-base leading-none" aria-hidden="true" />
            {{ t('newsletter.badge') }}
          </span>
        </div>

        <div class="space-y-2">
          <h3 class="text-center !text-2xl sm:!text-3xl">{{ t('newsletter.title') }}</h3>
          <p class="text-center !text-base">{{ t('newsletter.subtitle') }}</p>
        </div>

        <div v-if="!success" class="grid gap-4">
          <form class="grid gap-3" @submit.prevent="submit">
            <label class="sr-only" for="newsletter-email">{{ t('newsletter.emailLabel') }}</label>
            <SofaxInput
              id="newsletter-email"
              ref="emailInputRef"
              v-model="email"
              type="email"
              autocomplete="email"
              inputmode="email"
              :placeholder="t('newsletter.emailPlaceholder')"
            />
            <SofaxBtn
              type="submit"
              variant="accent"
              block
              :label="loading ? t('newsletter.ctaLoading') : t('newsletter.cta')"
              :disabled="loading"
            />
          </form>
          <!-- Sending the form is the consent, so its wording sits right under the button. -->
          <p class="text-center !text-xs text-muted dark:text-gray-400">
            {{ consentText }}
            <LocaleLink to="/privacy" target="_blank" class="link">{{ t('newsletter.privacyLink') }}</LocaleLink>
          </p>
          <SofaxNotice v-if="errorMsg" type="error">{{ errorMsg }}</SofaxNotice>
          <div class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center text-sm text-muted dark:text-gray-400">
            <span class="inline-flex items-center gap-2"><i class="bi bi-shield-check text-base" aria-hidden="true" />{{ t('newsletter.privacy') }}</span>
            <span class="inline-flex items-center gap-2"><i class="bi bi-people text-base" aria-hidden="true" />{{ t('newsletter.socialProof') }}</span>
          </div>
        </div>

        <div v-else class="grid place-items-center gap-3 py-2 text-center">
          <SofaxIconTile icon="bi-check2" round size="sm" />
          <SofaxNotice type="success" plain>{{ resultStatus === 'confirmed' ? t('newsletter.alreadySubscribed') : t('newsletter.success') }}</SofaxNotice>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
</style>
